import { c as _c } from "react-compiler-runtime";
import { useRef, useLayoutEffect } from 'react';
import { Metrics, FDT_METRIC_NAMES } from './Metrics';
import { useMetricsDimensions } from './metricsUtils';
import { useScrollWindowRef } from '../table/context/ScrollWindowRefContext';
import { observeVisibleLabelCells } from './observeVisibleLabelCells';

/**
 * Measures the time from when the row identity sequence changes to when
 * all visible label cells in the viewport have text content.
 *
 * This captures the re-render cost of user-driven data changes — page
 * navigation, sorting, filtering, or infinite-scroll appending rows.
 * Refreshes that return the same rows in the same order are ignored by
 * comparing a fingerprint of `rows.map(getRowId).join(',')`.
 *
 * How it works:
 * 1. A `useLayoutEffect` depending on `rows` computes a row-ID fingerprint.
 *    If it matches the previous fingerprint, the effect is a no-op.
 * 2. Otherwise it starts `observeVisibleLabelCells`, which snapshots
 *    `performance.now()` as the start time (still before browser paint,
 *    since this runs inside a layout effect) and uses IntersectionObserver +
 *    MutationObserver to detect when every visible label cell has text.
 *
 * We skip the first render (mount) because mount duration is already
 * covered by TBODY_MOUNT_TO_ALL_LABEL_CELLS_VISIBLE_TIME.
 */
export const useRowUpdateTimer = (rows, getRowId, pageable, pageSize) => {
  const $ = _c(6);
  const dims = useMetricsDimensions(pageable, pageSize);
  const {
    element: scrollElement
  } = useScrollWindowRef();
  const isFirstRender = useRef(true);
  const prevRowKeyRef = useRef("");
  let t0;
  let t1;
  if ($[0] !== dims || $[1] !== getRowId || $[2] !== rows || $[3] !== scrollElement) {
    t0 = () => {
      const rowKey = rows.map(getRowId).join(",");
      if (isFirstRender.current) {
        isFirstRender.current = false;
        prevRowKeyRef.current = rowKey;
        return;
      }
      if (rowKey === prevRowKeyRef.current) {
        return;
      }
      prevRowKeyRef.current = rowKey;
      if (!scrollElement) {
        return;
      }
      return observeVisibleLabelCells(scrollElement, duration => {
        Metrics.timer(FDT_METRIC_NAMES.ROW_UPDATE_TO_ALL_LABEL_CELLS_VISIBLE_TIME, dims).update(duration);
      });
    };
    t1 = [rows, getRowId, scrollElement, dims];
    $[0] = dims;
    $[1] = getRowId;
    $[2] = rows;
    $[3] = scrollElement;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useLayoutEffect(t0, t1);
};